import { HttpEvent, HttpEventType, HttpResponse, HttpUploadProgressEvent } from '@angular/common/http';
import { inject } from '@angular/core';
import { SharedLanguageService } from '@language';
import { DeleteToast, RecordsService, UpsertToast, waitForIndex } from '@mediahaven/shared';
import { Store } from '@ngrx/store';
import {
    AbstractUploadSandboxService,
    ApiError,
    ApiErrorCode,
    ApiFragment,
    ApiRecordType,
    CustomErrorType,
    FacialRecognitionConsent,
    FinalizedUploadStatuses,
    FragmentId,
    IUploadItem,
    MediaObjectId,
    Metadata,
    NotificationType,
    QueryPart,
    QueryPartsToStructuredQuery,
    QueryPartStrategy,
    RecordStatus,
    Restriction,
    ShToastFactory,
    Toast,
    UploadItem,
    UploadStatus,
    UrlQueryParamsConverter,
    ZoneId,
    ZoneRestrictions
} from '@stylehaven/components';
import { NEVER, Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { AddUploadItem, UpdateUploadItem } from '../../../state/actions/uploads.actions';
import { selectAll } from '../../../state/selectors/uploadItem.selector';
import { ApplicationState } from '../../../types/state';
import { REPRESENTATION_GROUP } from '../../constants/upload-item-group.constants';

export abstract class SharedUploadSandboxService extends AbstractUploadSandboxService {
    protected readonly recordsService: RecordsService = inject(RecordsService);
    protected readonly translateService: SharedLanguageService = inject(SharedLanguageService);
    protected readonly toastFactory: ShToastFactory = inject(ShToastFactory);
    protected readonly store: Store<ApplicationState> = inject(Store);

    protected readonly cancelUploadItem$: Subject<string> = new Subject<string>();

    private readonly INTERNAL_ERROR_CODE = 'INTERNAL_ERROR';
    private readonly MAX_FILE_SIZE_MB = 4000; // 4GB

    // Status to set UploadItem to after 100% upload and index check
    protected abstract readonly AFTER_DIRECT_UPLOAD_FINISHED_STATUS: UploadStatus;

    protected readonly PROGRESS_TOAST: Toast = this.toastFactory.create({
        type: NotificationType.Update,
        icon: 'spinner',
        iconRotate: true,
        autoClose: false,
        navigationLink: {
            route: '/concepts/uploads'
        },
        linkText: this.translateService.instant('toasts.see.details'),
        closable: true,
        message: this.translateService.instant('toasts.uploading')
    });

    private readonly uploadGroups: string[] = [];

    // #region Direct Upload
    protected directRecordUpload(
        file: File,
        uploadItem: IUploadItem,
        cancel$: Observable<string>,
        config: {
            zoneId: string;
            recordType: ApiRecordType;
            parentRecordId?: MediaObjectId;
            facialRecognitionConsent?: FacialRecognitionConsent;
        }
    ): Observable<HttpEvent<unknown>> {
        if (this.isFileTooLarge(file)) {
            return this.handleFileTooLarge(uploadItem.tempId);
        }

        return this.recordsService
            .uploadFile(
                file,
                config.zoneId as ZoneId,
                config.recordType,
                config.parentRecordId,
                config.facialRecognitionConsent
            )
            .pipe(
                takeUntil(cancel$),
                catchError(error => {
                    this.handleHttpError(uploadItem.tempId, error.error);
                    return of(error);
                }),
                tap((event: HttpEvent<any>) => {
                    this.handleHttpEvent(event, uploadItem.tempId);
                })
            );
    }

    protected directRepresentationUpload(
        file: File,
        uploadItem: IUploadItem,
        cancel$: Observable<string>,
        fragmentId: FragmentId
    ): Observable<HttpEvent<unknown>> {
        if (this.isFileTooLarge(file)) {
            return this.handleFileTooLarge(uploadItem.tempId);
        }

        return this.recordsService.uploadRepresentation(file, fragmentId).pipe(
            takeUntil(cancel$),
            catchError(error => {
                this.handleHttpError(uploadItem.tempId, error.error);
                return of(error);
            }),
            tap((event: HttpEvent<any>) => {
                this.handleHttpEvent(event, uploadItem.tempId);
            })
        );
    }
    // #endregion

    // #region After upload handlers
    protected uploadToApiFragment(uploadEvent$: Observable<HttpEvent<unknown>>): Observable<ApiFragment> {
        return uploadEvent$.pipe(
            filter((event: HttpEvent<any>) => Object.prototype.hasOwnProperty.call(event, 'body')),
            map(event => event as HttpResponse<any>),
            filter(event => !!event.body),
            map(event => event as { body: Metadata }),
            map(event => new ApiFragment(event.body))
        );
    }

    protected checkIndexed(fragments: ApiFragment[]): Observable<ApiFragment[]> {
        const restriction: Restriction = ZoneRestrictions.fullZoneSearch();
        const fragmentIds: FragmentId[] = fragments.map(fragment => fragment.getFragmentId());

        return this.recordsService.countRecords(fragmentIds, restriction).pipe(
            map(count => {
                if (count < fragmentIds.length) {
                    throw CustomErrorType.IndexTimeout;
                }
                return true;
            }),
            waitForIndex(),
            map(() => fragments)
        );
    }
    // #endregion

    // #region Http event handlers
    private handleHttpEvent(event: HttpEvent<any>, uploadItemId: string): void {
        switch (event.type) {
            case HttpEventType.UploadProgress:
                this.handleUploadProgressType(event, uploadItemId);
                break;
            case HttpEventType.Response:
                this.handleResponseType(event, uploadItemId);
                break;
        }
    }

    private handleUploadProgressType(event: HttpEvent<any>, uploadItemId: string): void {
        const progressEvent: HttpUploadProgressEvent = event as HttpUploadProgressEvent;
        const uploadProgress: number = progressEvent.total
            ? Math.round((100 * progressEvent.loaded) / progressEvent.total)
            : 0;

        const uploadItemUpdate: Partial<UploadItem> = {
            state: {
                status: UploadStatus.UPLOADING,
                progress: uploadProgress
            },
            lastProgressUpdate: new Date()
        };

        this.syncUploadItem(uploadItemId, uploadItemUpdate);
    }

    protected handleResponseType(event: HttpEvent<any>, uploadItemId: string): void {
        const responseEvent: HttpResponse<Metadata> = event as HttpResponse<Metadata>;

        // MED-3752 Refreshing while uploading does not throw a clear error, but returns an error in the body
        if (typeof responseEvent.body === 'string') {
            this.cancelOrAbortUpload({ tempId: uploadItemId } as IUploadItem, true);
            return;
        }

        const fragment: ApiFragment | undefined = responseEvent.body ? new ApiFragment(responseEvent.body) : undefined;

        if (fragment) {
            const uploadItemUpdate: Partial<IUploadItem> = {
                id: fragment.getFragmentId(),
                state: {
                    status: this.AFTER_DIRECT_UPLOAD_FINISHED_STATUS,
                    progress: 100
                }
            };

            this.syncUploadItem(uploadItemId, uploadItemUpdate);
        }
    }

    protected handleHttpError(uploadItemId: string, error: ApiError): Observable<never> {
        let message: string;

        if (error.Code === ApiErrorCode.EDUPLICATE) {
            const errorMessage: string = this.translateService.instant('uploads.list.errors.upload-duplicate');
            const recordIds: string[] = error.ExistingRecordIds ?? [];
            const duplicateUrls: string[] = recordIds.map(id => `records/${id}`);
            const duplicateLinks: string = duplicateUrls
                .map(
                    url =>
                        `<a class="sh-link" href="${url}">${this.translateService.instant(
                            'uploads.list.show-duplicate'
                        )}</a>`
                )
                .join('<br />');

            message = `${errorMessage}<br />${duplicateLinks}`;
        } else if (error.Code === this.INTERNAL_ERROR_CODE) {
            message = error.Message;
        } else {
            message = this.translateService.instant('uploads.list.errors.internal');
        }

        const uploadItemUpdate: Partial<IUploadItem> = {
            state: {
                status: error.Code === ApiErrorCode.EDUPLICATE ? UploadStatus.REJECTED : UploadStatus.FAILED,
                progress: 100,
                message
            }
        };

        this.syncUploadItem(uploadItemId, uploadItemUpdate);

        if (uploadItemUpdate.group !== REPRESENTATION_GROUP) {
            const toast: Toast = this.toastFactory.create({
                message: this.translateService.instant('toasts.upload-failed'),
                navigationLink: {
                    route: '/concepts/uploads'
                },
                linkText: this.translateService.instant('toasts.see.details'),
                type: NotificationType.Error
            });
            this.store.dispatch(new UpsertToast(toast));
        }

        return NEVER;
    }

    protected handleUploadTimeout(upload: IUploadItem): IUploadItem {
        const toastKey: string = 'errors.index-timeout';
        const errorKey: string = 'uploads.list.errors.timeout';

        const session = {
            file: upload.session?.file as File,
            sessionId: upload.session?.sessionId
        };

        const uploadItemUpdate: IUploadItem = {
            ...upload,
            state: {
                // MED-6465: If already specific error state, keep status, else set update status or default to Failed
                status: UploadStatus.INDEX_TIMEOUT,
                message: this.translateService.instant(errorKey),
                progress: 100
            },
            session
        };

        this.syncUploadItem(upload.tempId, uploadItemUpdate);

        if (uploadItemUpdate.group !== REPRESENTATION_GROUP) {
            const toast: Toast = this.toastFactory.create({
                message: this.translateService.instant(toastKey),
                type: NotificationType.Warning
            });
            this.store.dispatch(new UpsertToast(toast));
        }

        return uploadItemUpdate;
    }

    // #endregion

    // #region File too large
    private isFileTooLarge(file: File): boolean {
        const fileSizeMB = file.size / (1024 * 1024);
        return fileSizeMB > this.MAX_FILE_SIZE_MB;
    }

    private handleFileTooLarge(uploadItemId: string): Observable<never> {
        return this.handleHttpError(uploadItemId, {
            Code: this.INTERNAL_ERROR_CODE,
            Message: this.translateService.instant('uploads.list.errors.upload-too-large', {
                size: this.MAX_FILE_SIZE_MB
            }),
            Status: 400
        });
    }
    // #endregion

    // #region UploadItem

    protected initUploadItem(uploadItem: IUploadItem): void {
        this.store.dispatch(new AddUploadItem(uploadItem));
    }

    protected syncUploadItem(id: string, partialUploadItem: Partial<IUploadItem>): void {
        this.store.dispatch(new UpdateUploadItem(id, partialUploadItem));
    }

    protected getAllUploadItems(): Observable<IUploadItem[]> {
        return this.store.select(selectAll);
    }
    // #endregion

    // #region UploadItem Listeners
    protected initProgressListener(): void {
        const isBusy$: Observable<boolean> = this.getAllUploadItems().pipe(
            map(
                (items: IUploadItem[]) =>
                    !items.every((item: IUploadItem) => FinalizedUploadStatuses.includes(item.state.status))
            ),
            distinctUntilChanged()
        );

        isBusy$.subscribe(isBusy => {
            if (isBusy) {
                this.dispatchProgressToast();
            } else {
                this.deleteProgressToast();
            }
        });
    }
    // #endregion

    // #region Toasts
    protected startGroupToast(group: string): void {
        if (this.uploadGroups.includes(group)) {
            return;
        }
        this.uploadGroups.push(group);

        this.dispatchProgressToast();
    }

    protected finishGroupToast(group: string) {
        const queryParts: QueryPart[] = [
            new QueryPart({
                field: 'Administrative.RecordStatus',
                strategy: QueryPartStrategy.MatchAtLeastOne,
                values: [RecordStatus.Processing, RecordStatus.Rejected]
            })
        ];

        const finishedToast: Toast = this.toastFactory.create({
            message: this.translateService.instant('toasts.upload-complete'),
            navigationLink: {
                route: '/concepts',
                extras: {
                    queryParams: UrlQueryParamsConverter.generateQueryParams(QueryPartsToStructuredQuery(queryParts))
                }
            },
            linkText: this.translateService.instant('toasts.see.processing'),
            type: NotificationType.Success
        });

        const allUploadItems$: Observable<IUploadItem[]> = this.store.select(selectAll);

        const groupItems$: Observable<IUploadItem[]> = allUploadItems$.pipe(
            map(uploadItems => uploadItems.filter(uploadItem => uploadItem.group === group))
        );

        const allFinished$: Observable<boolean> = groupItems$.pipe(
            map(items => items.every(item => FinalizedUploadStatuses.includes(item.state.status))),
            filter(items => items)
        );

        allFinished$
            .pipe(
                withLatestFrom(groupItems$),
                take(1),
                map(([_, items]) => items.map(item => [item.state.status, item.group !== REPRESENTATION_GROUP])),
                map(result =>
                    result.some(
                        ([status, uploadIsNotRepresentation]) =>
                            status === UploadStatus.FINISHED && uploadIsNotRepresentation
                    )
                )
            )
            .subscribe(hasSuccess => {
                if (hasSuccess) {
                    this.store.dispatch(new UpsertToast(finishedToast));
                }
            });
    }

    protected dispatchFinishedToast() {
        const queryParts: QueryPart[] = [
            new QueryPart({
                field: 'Administrative.RecordStatus',
                strategy: QueryPartStrategy.MatchAtLeastOne,
                values: [RecordStatus.Processing, RecordStatus.Rejected, RecordStatus.Uploaded]
            })
        ];

        const finishedToast: Toast = this.toastFactory.create({
            message: this.translateService.instant('toasts.upload-complete'),
            navigationLink: {
                route: '/concepts',
                extras: {
                    queryParams: UrlQueryParamsConverter.generateQueryParams(QueryPartsToStructuredQuery(queryParts))
                }
            },
            linkText: this.translateService.instant('toasts.see.processing'),
            type: NotificationType.Success
        });
        this.store.dispatch(new UpsertToast(finishedToast));
    }

    protected dispatchProgressToast(): void {
        this.store.dispatch(new UpsertToast(this.PROGRESS_TOAST));
    }

    protected deleteProgressToast(): void {
        this.store.dispatch(new DeleteToast(this.PROGRESS_TOAST.id));
    }
    // #endregion

    // #region cancel
    protected initCancelUploadItemStream(uploadItem: IUploadItem): Observable<string> {
        return this.cancelUploadItem$.pipe(
            filter(uploadItemId => uploadItemId === uploadItem.tempId),
            tap(uploadItemId => {
                const uploadItemUpdate: Partial<IUploadItem> = {
                    state: {
                        status: UploadStatus.CANCELLED,
                        progress: 100
                    }
                };
                this.syncUploadItem(uploadItemId, uploadItemUpdate);
            })
        );
    }

    public cancelOrAbortUpload(uploadItem: IUploadItem, abort?: boolean): void {
        if (!uploadItem) {
            return;
        }
        const status: UploadStatus = abort ? UploadStatus.ABORTED : UploadStatus.CANCELLED;

        const tempId: string = uploadItem.tempId;
        const uploadItemUpdate: Partial<IUploadItem> = {
            state: {
                status,
                progress: 100
            }
        };
        const syncData: { tempId: string; uploadItemUpdate: Partial<IUploadItem> } = { tempId, uploadItemUpdate };

        if (abort) {
            this.syncUploadItem(tempId, uploadItemUpdate);
        } else {
            this.cancelUpload(syncData);
        }
    }
    // #endregion
}
